/**
 * The live side of `threadProjection`: one projection per thread that a
 * mounted view shows, fed by the query cache (`loaded`) and the hub's
 * `thread.token` blocks. A token for a thread no view shows is dropped
 * (spec §7.4); the view's first fetch covers it.
 *
 * Slice 1q: also a running turn's live draft and Working state (`thread.draft`,
 * `turn.live`), and its reply so far (`GET /api/threads/:id/live`), fetched
 * while the thread shows a running turn, on a reconnect, and on a hole. A
 * mounted view watches its thread (../../app/watch).
 */
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { api } from "../../app/api";
import { holdWatch } from "../../app/watch";
import { threadKey, useThread, type ThreadDetail } from "./api";
import {
  emptyProjection,
  threadProjection,
  type Block,
  type DraftPiece,
  type Projection,
  type ProjectionAction,
  type SoFar,
  type TurnLive,
} from "./projection";

/** A refetch that still leaves a gap tries again, up to this many times in a row. */
const MAX_GAP_REFETCHES = 3;

type Entry = {
  state: Projection;
  listeners: Set<() => void>;
  refetches: number;
  /** A render is due on the next frame (drafts come up to 10 a second). */
  frame: number | null;
};

/** The reply so far of a thread's running turn (slice 1q §5); with no id, every thread's. */
export const liveKey = (threadId?: string) =>
  threadId ? ["thread-live", threadId] : ["thread-live"];

const notify = (entry: Entry) => {
  for (const listener of entry.listeners) listener();
};

/** Tells the views now, or (for a draft) once a frame, so a long reply doesn't render 10 times a second. */
function changed(entry: Entry, soon: boolean) {
  if (!soon || typeof requestAnimationFrame === "undefined") {
    if (entry.frame !== null) cancelAnimationFrame(entry.frame);
    entry.frame = null;
    return notify(entry);
  }
  entry.frame ??= requestAnimationFrame(() => {
    entry.frame = null;
    notify(entry);
  });
}

const entries = new Map<string, Entry>();

/**
 * How many gap refetches in a row after a change: reset once the gap is
 * filled, +1 when the projection has just gone stale or a refetch left it
 * stale (up to the cap). The caller refetches when the count goes up.
 */
export function gapRefetches(
  prev: Projection,
  next: Projection,
  action: ProjectionAction,
  count: number,
): number {
  if (!next.stale) return 0;
  const newly = !prev.stale || action.type === "loaded";
  return newly && count < MAX_GAP_REFETCHES ? count + 1 : count;
}

function dispatch(client: QueryClient, threadId: string, action: ProjectionAction) {
  const entry = entries.get(threadId);
  if (!entry) return;
  const prev = entry.state;
  const next = threadProjection(prev, action);
  if (action.type !== "loaded" && action.type !== "block") {
    return settleLive(client, threadId, entry, next, action.type === "draft");
  }
  // A refetch that came back unchanged still counts: a gap it didn't fill asks again.
  if (next === prev && action.type === "block") return;
  const count = gapRefetches(prev, next, action, entry.refetches);
  if (count > entry.refetches) void client.invalidateQueries({ queryKey: threadKey(threadId) });
  entry.refetches = count;
  if (next === prev) return;
  entry.state = next;
  changed(entry, false);
}

/** A live action's result (slice 1q §6.2): a hole fetches the reply so far; a draft renders once a frame. */
function settleLive(
  client: QueryClient,
  threadId: string,
  entry: Entry,
  next: Projection,
  draft: boolean,
) {
  const prev = entry.state;
  if (next.staleLive && !prev.staleLive) {
    void client.invalidateQueries({ queryKey: liveKey(threadId) });
  }
  if (next === prev) return;
  entry.state = next;
  changed(entry, draft);
}

/** `thread.draft` from the hub (slice 1q §2.1). */
export function pushDraft(client: QueryClient, threadId: string, draft: DraftPiece) {
  dispatch(client, threadId, { type: "draft", draft });
}

/** `turn.live` from the hub (slice 1q §2.2). */
export function pushLive(client: QueryClient, threadId: string, live: TurnLive) {
  dispatch(client, threadId, { type: "live", live });
}

/** `thread.token` from the hub. */
export function pushBlock(client: QueryClient, threadId: string, block: Block) {
  dispatch(client, threadId, { type: "block", block });
}

/**
 * A view's hold on a thread's projection. The entry lives while any view
 * holds it, and goes with the last one: tokens for it are dropped from then on.
 */
export function subscribeThread(threadId: string, listener: () => void) {
  let entry = entries.get(threadId);
  if (!entry) {
    entry = { state: emptyProjection, listeners: new Set(), refetches: 0, frame: null };
    entries.set(threadId, entry);
  }
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size > 0) return;
    if (entry.frame !== null) cancelAnimationFrame(entry.frame);
    entries.delete(threadId);
  };
}

export const threadSnapshot = (threadId: string) => entries.get(threadId)?.state ?? emptyProjection;

/** The `['thread', id]` query's data, into the projection. */
export function loadThread(client: QueryClient, threadId: string, thread: ThreadDetail) {
  dispatch(client, threadId, { type: "loaded", thread });
}

/**
 * The projected thread for a view: the `['thread', id]` query plus the live
 * blocks since. Chats and the side panel both call this.
 *
 * - The subscription is stable per thread. A new one on every render would
 *   drop the entry, and its live blocks, each time the view rerendered.
 * - Mounting always refetches: tokens sent while no view held the thread were
 *   dropped, so cached data from before can be missing blocks.
 * - Each finished fetch is loaded, even an unchanged one (see `dispatch`).
 */
export function useThreadProjection(threadId: string) {
  const client = useQueryClient();
  const query = useThread(threadId, { refetchOnMount: "always" });
  const subscribe = useCallback(
    (listener: () => void) => subscribeThread(threadId, listener),
    [threadId],
  );
  const getSnapshot = useCallback(() => threadSnapshot(threadId), [threadId]);
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const data: ThreadDetail | undefined = query.data;
  // Every finished fetch, not only changed data: TanStack keeps the same `data`
  // object when a refetch returns the same thread, and that still settles a gap retry.
  const fetchedAt = query.dataUpdatedAt;
  useEffect(() => {
    if (data && fetchedAt > 0) loadThread(client, threadId, data);
  }, [client, threadId, data, fetchedAt]);
  // Slice 1q: this view's chat gets its live drafts, and a running turn's reply so far.
  useEffect(() => holdWatch(threadId), [threadId]);
  const running = Boolean(data?.turns.some((t) => t.status === "running"));
  const soFar = useQuery({
    queryKey: liveKey(threadId),
    queryFn: () => api<{ live: SoFar }>("GET", `/api/threads/${threadId}/live`),
    enabled: running,
    staleTime: 0,
    gcTime: 0,
  });
  const soFarAt = soFar.dataUpdatedAt;
  const live = soFar.data?.live;
  useEffect(() => {
    if (soFarAt > 0 && live !== undefined) {
      dispatch(client, threadId, { type: "so_far", live });
    }
  }, [client, threadId, live, soFarAt]);
  // Until the effect lands, project the fresh data directly.
  const projection =
    data && state.loaded !== data
      ? threadProjection(state, { type: "loaded", thread: data })
      : state;
  return { query, projection };
}
